<template>
    <UiModal :is-open="showProgramModal" @close="closeModal()" width="600px">
        <template #title>
            <div class="flex-1">
                <div>
                    <h3
                        class="text-xl font-medium text-slate-800 dark:text-slate-100 font-['Inter'] leading-7"
                        v-if="step !== 5"
                    >
                        Add program
                    </h3>
                    <p
                        v-if="stepSubtitle"
                        class="text-sm font-normal leading-5 text-slate-600 dark:text-slate-300 mt-1"
                    >
                        {{ stepSubtitle }}
                    </p>
                </div>
            </div>
        </template>
        <template #content>
            <div
                class="items-start py-2"
                :class="{ 'overflow-hidden h-auto pb-0': step === 5 }"
            >
                <div class="steps" :class="{ '!h-auto': step === 5 }">
                    <StepUserType
                        ref="stepUserTypeRef"
                        v-show="step === 0"
                        :program="program"
                        :rewards="rewards"
                        :errors="errors"
                        :filtered-program-types="filteredProgramTypes"
                        :show-user-dropdown="showUserDropdown"
                        :dropdown-top="dropdownTop"
                        :dropdown-left="dropdownLeft"
                        :dropdown-width="dropdownWidth"
                        @toggle-user-dropdown="handleToggleUserDropdown"
                        @close-user-dropdown="closeUserDropdown"
                        @select-user="selectUser"
                        @update:program-type="program.type = $event"
                    />
                    <StepProgramSearch
                        v-show="step === 1"
                        :program="program"
                        :program-search-query="programSearchQuery"
                        :filtered-program-choices="filteredProgramChoices"
                        :can-choose-program-choice="canChooseProgramChoice"
                        :get-program-image-url="getProgramImageUrl"
                        @update:program-search-query="programSearchQuery = $event"
                        @update:program-id="program.program_id = $event"
                        @show-tooltip="showTooltip"
                        @hide-tooltip="hideTooltip"
                    />
                    <StepUpdateMethod
                        v-show="step === 2"
                        :update-method="updateMethod"
                        @update:update-method="updateMethod = $event"
                    />
                    <StepBalance
                        v-show="step === 3"
                        :program="program"
                        :chosen-program="chosenProgram"
                        :formatted-points="formattedPoints"
                        :errors="errors"
                        :get-program-image-url="getProgramImageUrl"
                        @update:formatted-points="formattedPoints = $event"
                        @clear-error="clearError"
                    />
                    <StepEliteStatus
                        v-show="step === 4"
                        :program="program"
                        :chosen-program="chosenProgram"
                        :get-program-image-url="getProgramImageUrl"
                        @update:has-elite-status="program.has_elite_status = $event"
                        @update:elite-status="program.elite_status = $event"
                    />
                    <StepSuccess
                        v-if="step === 5"
                        :chosen-program="chosenProgram"
                        :formatted-points="formattedPoints"
                        :program-type="program.type"
                        :get-program-image-url="getProgramImageUrl"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <div
                class="flex flex-row flex-wrap justify-between items-center pt-0"
            >
                <Errors :errors="errors" class="mb-2" />
                <div
                    class="w-full mb-3 flex items-center justify-between"
                    v-if="step !== 5"
                >
                    <div class="w-1/4">
                        <button
                            v-if="step > 0"
                            type="button"
                            class="btn-base btn-white"
                            @click.prevent="prevStep()"
                        >
                            Back
                        </button>
                    </div>
                    <div class="w-1/2">
                        <ul
                            class="w-full flex justify-center items-center gap-2"
                        >
                            <li
                                v-for="stepIndex in totalSteps"
                                :key="'step-' + stepIndex"
                                class="w-6 h-1 rounded-sm"
                                :class="{
                                    'bg-slate-800':
                                        stepIndex === step + 1,
                                    'bg-slate-200 dark:bg-slate-700':
                                        stepIndex !== step + 1,
                                }"
                            ></li>
                        </ul>
                    </div>
                    <div class="w-1/4 flex gap-2 justify-end">
                        <button
                            type="button"
                            class="btn-base btn-white"
                            @click.prevent="closeModal()"
                        >
                            Cancel
                        </button>
                        <ActionButton
                            v-if="canSaveStep"
                            @clicked="addReward"
                            label="Add"
                            :loading="saveProgramBtnAction"
                            classes="btn-base btn-dark"
                        />
                        <button
                            v-if="canNextStep"
                            type="button"
                            class="btn-base btn-dark"
                            @click.prevent="nextStep()"
                        >
                            Next
                        </button>
                    </div>
                </div>

                <div
                    v-if="step === 5"
                    class="w-full flex justify-end gap-4 pb-5"
                >
                    <button
                        type="button"
                        class="btn-base btn-white"
                        @click.prevent="addAnotherReward()"
                    >
                        Add Another Program
                    </button>

                    <button
                        type="button"
                        class="btn-base btn-10x-orange"
                        @click.prevent="closeModal()"
                    >
                        Finish
                    </button>
                </div>
            </div>
        </template>
    </UiModal>
    <teleport to="body">
        <div
            v-if="tooltipVisible"
            class="fixed inline-flex px-2 py-1 flex-col items-start rounded-lg bg-slate-800 shadow-sm"
            style="z-index: 999999; pointer-events: none"
            :style="tooltipStyle"
        >
            <span
                class="font-['Inter'] text-sm font-normal leading-5 text-white"
            >
                Program already added
            </span>
        </div>
    </teleport>
</template>

<script setup>
import { ref } from 'vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import StepUserType from '@/components/mypoints/modals/add-reward/StepUserType.vue';
import StepProgramSearch from '@/components/mypoints/modals/add-reward/StepProgramSearch.vue';
import StepUpdateMethod from '@/components/mypoints/modals/add-reward/StepUpdateMethod.vue';
import StepBalance from '@/components/mypoints/modals/add-reward/StepBalance.vue';
import StepEliteStatus from '@/components/mypoints/modals/add-reward/StepEliteStatus.vue';
import StepSuccess from '@/components/mypoints/modals/add-reward/StepSuccess.vue';
import { useAddRewardWizard } from '@/composables/useAddRewardWizard';

const stepUserTypeRef = ref(null);

function handleToggleUserDropdown() {
    const btnEl = stepUserTypeRef.value?.userDropdownBtn;
    toggleUserDropdown(btnEl);
}

const {
    // State
    step,
    errors,
    saveProgramBtnAction,
    programSearchQuery,
    showUserDropdown,
    dropdownTop,
    dropdownLeft,
    dropdownWidth,
    tooltipVisible,
    tooltipStyle,
    updateMethod,
    program,

    // Store refs
    rewards,
    showProgramModal,

    // Computed
    filteredProgramTypes,
    chosenProgram,
    filteredProgramChoices,
    canChooseProgramChoice,
    canSaveStep,
    canNextStep,
    totalSteps,
    stepSubtitle,
    formattedPoints,

    // Methods
    getProgramImageUrl,
    nextStep,
    prevStep,
    addReward,
    addAnotherReward,
    closeModal,
    toggleUserDropdown,
    selectUser,
    closeUserDropdown,
    clearError,
    showTooltip,
    hideTooltip,
} = useAddRewardWizard();
</script>

<style lang="scss" scoped>
.steps {
    position: relative;

    :deep(.programs-list) {
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
        }

        label {
            .border-b {
                border-color: #e2e8f0;
            }
        }
    }

    height: 100%;
}

.step {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

:deep(select) {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
}

:deep(select:focus) {
    outline: none;
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
}

:deep(select option) {
    padding: 8px;
    background-color: white;
}

:deep(.svg-background) {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -100px;
    width: 100%;
    height: 100%;
    z-index: 0;
    overflow: visible;
    display: flex;
    justify-content: center;
}
</style>
